import React from 'react';
import LegalPageLayout from './LegalPageLayout';

const CommunityStandardsPage: React.FC = () => (
  <LegalPageLayout title="Community Standards" lastUpdated="June 2026">
    <h2>Introduction</h2>
    <p>
      SkipScreen is designed to help candidates showcase who they are before the first conversation
      and help recruiters gain meaningful insights into candidates through professional portfolios,
      Screening Prompts, accomplishments, certifications, messaging, and communication tools.
    </p>
    <p>
      To maintain a safe, professional, and trustworthy environment, all users are expected to
      follow these Community Standards.
    </p>
    <p>
      By using SkipScreen, you agree to comply with these standards in addition to our Terms of
      Service and Privacy Policy.
    </p>

    <h2>Our Principles</h2>
    <p>SkipScreen is built around:</p>
    <ul>
      <li>Professionalism</li>
      <li>Honesty</li>
      <li>Respect</li>
      <li>Transparency</li>
      <li>Candidate control</li>
      <li>Meaningful communication</li>
    </ul>
    <p>
      Users are expected to engage with the platform in a manner consistent with these principles.
    </p>

    <h2>Professional Conduct</h2>
    <p>Users must interact with others respectfully and professionally at all times.</p>
    <p>You may not:</p>
    <ul>
      <li>Harass, intimidate, threaten, or abuse other users</li>
      <li>Use discriminatory, hateful, or offensive language</li>
      <li>Engage in bullying or targeted harassment</li>
      <li>Send unwanted or inappropriate messages</li>
      <li>Use the platform to promote violence or unlawful activity</li>
    </ul>
    <p>
      SkipScreen reserves the right to remove content or restrict access to users who violate these
      standards.
    </p>

    <h2>Authentic Profiles and Information</h2>
    <p>
      Users are responsible for ensuring that all information submitted to SkipScreen is accurate
      and truthful.
    </p>
    <p>You may not:</p>
    <ul>
      <li>Create fake profiles</li>
      <li>Impersonate another individual</li>
      <li>Misrepresent your identity</li>
      <li>Misrepresent employment history</li>
      <li>Misrepresent educational background</li>
      <li>Misrepresent certifications or qualifications</li>
      <li>Upload fraudulent or altered credentials</li>
    </ul>
    <p>
      Trust is critical to the SkipScreen platform. Content found to be intentionally misleading may
      be removed and accounts may be suspended or terminated.
    </p>

    <h2>Screening Prompts and Video Content</h2>
    <p>
      Screening Prompt responses should accurately represent your experience, qualifications, and
      professional background.
    </p>
    <p>Users may not:</p>
    <ul>
      <li>Submit deceptive or fraudulent responses</li>
      <li>Use content that infringes on another person&apos;s rights</li>
      <li>Upload inappropriate, obscene, or unlawful content</li>
      <li>Upload content intended to harass, threaten, or deceive others</li>
    </ul>
    <p>SkipScreen encourages users to present themselves authentically and professionally.</p>

    <h2>Resume, Portfolio, and Document Uploads</h2>
    <p>
      Users may upload resumes, certifications, accomplishments, projects, and supporting documents.
    </p>
    <p>Uploaded materials must:</p>
    <ul>
      <li>Belong to the user or be uploaded with proper authorization</li>
      <li>Be accurate and truthful</li>
      <li>Not violate intellectual property rights</li>
      <li>Not contain unlawful or harmful content</li>
    </ul>
    <p>
      Users are responsible for ensuring they have the right to upload any content submitted to the
      platform.
    </p>

    <h2>Messaging and Communication</h2>
    <p>
      SkipScreen&apos;s messaging and communication tools are intended to facilitate professional
      interactions between candidates and recruiters.
    </p>
    <p>Users may not:</p>
    <ul>
      <li>Send spam or unsolicited commercial messages</li>
      <li>Harass or threaten other users</li>
      <li>Share malicious links</li>
      <li>Attempt to collect personal information through deceptive means</li>
      <li>Engage in fraudulent recruiting activity</li>
      <li>Use the platform for scams or impersonation</li>
    </ul>
    <p>Professional communication is expected at all times.</p>

    <h2>Artificial Intelligence and Generated Content</h2>
    <p>
      Users may use AI tools to assist with preparing content, provided the information presented
      remains truthful and accurately reflects the user&apos;s experience and qualifications.
    </p>
    <p>Users may not:</p>
    <ul>
      <li>Use AI-generated content to misrepresent credentials or accomplishments</li>
      <li>Fabricate employment history or qualifications</li>
      <li>Present false information as factual</li>
    </ul>
    <p>SkipScreen reserves the right to remove content determined to be deceptive or misleading.</p>

    <h2>Privacy and Data Respect</h2>
    <p>Users must respect the privacy of others.</p>
    <p>You may not:</p>
    <ul>
      <li>Share another user&apos;s personal information without permission</li>
      <li>Attempt to access another user&apos;s account</li>
      <li>Collect or scrape user data without authorization</li>
      <li>Use platform information for unauthorized marketing or solicitation</li>
    </ul>
    <p>Unauthorized access or misuse of data may result in immediate account termination.</p>

    <h2>Platform Integrity</h2>
    <p>To protect the platform and its users, you may not:</p>
    <ul>
      <li>Attempt to bypass platform security measures</li>
      <li>Reverse engineer platform functionality</li>
      <li>Use bots or automated systems to create accounts or collect information</li>
      <li>Interfere with platform operations</li>
      <li>Distribute malware or malicious code</li>
      <li>Conduct unauthorized security testing</li>
    </ul>

    <h2>Reporting Violations</h2>
    <p>Users may report content, behavior, or accounts that violate these Community Standards.</p>
    <p>Reports may include:</p>
    <ul>
      <li>Fraudulent profiles</li>
      <li>Fake credentials</li>
      <li>Harassment</li>
      <li>Spam</li>
      <li>Security concerns</li>
      <li>Misuse of the platform</li>
    </ul>
    <p>
      Reports can be submitted by contacting:{' '}
      <a href="mailto:contact@useskipscreen.com">contact@useskipscreen.com</a>
    </p>

    <h2>Enforcement</h2>
    <p>
      SkipScreen reserves the right to investigate violations of these Community Standards and take
      appropriate action.
    </p>
    <p>Actions may include:</p>
    <ul>
      <li>Content removal</li>
      <li>Warning notices</li>
      <li>Temporary account suspension</li>
      <li>Permanent account termination</li>
      <li>Restriction of platform access</li>
      <li>Reporting unlawful activity to appropriate authorities when required</li>
    </ul>
    <p>Enforcement decisions are made at SkipScreen&apos;s sole discretion.</p>

    <h2>Our Commitment</h2>
    <p>
      SkipScreen&apos;s goal is to create a professional environment where candidates can
      confidently showcase their experience and recruiters can engage with candidates in a
      trustworthy and meaningful way.
    </p>
    <p>
      We believe hiring works best when communication is respectful, information is accurate, and
      users are empowered to present their authentic professional story.
    </p>
    <p>Thank you for helping us maintain a safe and professional community.</p>
  </LegalPageLayout>
);

export default CommunityStandardsPage;
